<script lang="ts">
  import { Quirofano, Episodios, Pacientes, Consultorios, type Sala, type Cirugia, type Episodio, type Profesional } from '../lib/api';
  import PacientePicker from './PacientePicker.svelte';
  import { mapaNombresPaciente, etiquetaVisible } from '../lib/nombres';

  let salas = $state<Sala[]>([]);
  let agenda = $state<Cirugia[]>([]);
  let episodios = $state<Episodio[]>([]);
  let loading = $state(false);
  let error = $state<string | null>(null);

  // ventana de agenda: hoy → +7 días, en UTC ISO con 'Z'
  const _now = new Date();
  const _in7 = new Date(_now.getTime() + 7 * 86400_000);
  const _iso = (d: Date) => d.toISOString().replace(/\.\d+Z$/, 'Z');
  let desde = $state(_iso(_now));
  let hasta = $state(_iso(_in7));

  let nuevaSala = $state<Sala>({ codigo: '', nombre: '', estado: 'DISPONIBLE' });
  let nuevaCir = $state<Cirugia>({
    pacienteId: '', salaId: '', procedimiento: '',
    fechaProgramada: new Date(Date.now() + 3600_000).toISOString().slice(0, 16),
    duracionEstimadaMin: 60
  });
  let nombresPac = $state<Record<string, string>>({});
  let profesionales = $state<Profesional[]>([]);

  async function cargar() {
    loading = true; error = null;
    try {
      [salas, agenda] = await Promise.all([Quirofano.salas(), Quirofano.agenda(desde, hasta)]);
      const [pacs, profs] = await Promise.all([
        Pacientes.list().catch(() => []),
        Consultorios.profesionales().catch(() => []),
      ]);
      nombresPac = mapaNombresPaciente(pacs);
      profesionales = profs;
    } catch (e: any) { error = e?.message ?? String(e); }
    finally { loading = false; }
  }

  async function crearSala(e: SubmitEvent) {
    e.preventDefault();
    try { await Quirofano.crearSala({ ...nuevaSala }); nuevaSala = { codigo: '', nombre: '', estado: 'DISPONIBLE' }; await cargar(); }
    catch (e: any) { error = e?.message ?? String(e); }
  }

  async function programar(e: SubmitEvent) {
    e.preventDefault();
    if (!nuevaCir.pacienteId || !nuevaCir.salaId) { error = 'Paciente y sala son obligatorios.'; return; }
    try {
      await Quirofano.programar({
        ...nuevaCir,
        episodioId: nuevaCir.episodioId || null,
        fechaProgramada: _iso(new Date(nuevaCir.fechaProgramada))
      });
      nuevaCir = { ...nuevaCir, pacienteId: '', procedimiento: '', episodioId: null };
      episodios = [];
      await cargar();
    } catch (e: any) { error = e?.message ?? String(e); }
  }

  async function cargarEpisodios(pid: string) {
    if (!pid) { episodios = []; return; }
    try {
      const todos = await Episodios.porPaciente(pid);
      episodios = todos.filter(e => e.estado === 'ABIERTO' || e.estado === 'EN_ATENCION');
    } catch { episodios = []; }
  }

  $effect(() => { cargar(); });
  $effect(() => { cargarEpisodios(nuevaCir.pacienteId); });
</script>

<section>
  <header class="view-header">
    <div><h2>Quirófano</h2><p class="muted">Salas, programación y agenda de cirugías</p></div>
    <button onclick={cargar} disabled={loading}>{loading ? '…' : 'Recargar'}</button>
  </header>
  {#if error}<p class="err">⚠ {error}</p>{/if}

  <h3>Salas</h3>
  <form onsubmit={crearSala} class="row">
    <label class="field"><span>Código</span><input placeholder="Código (Q-1)" bind:value={nuevaSala.codigo} required /></label>
    <label class="field"><span>Nombre</span><input placeholder="Nombre" bind:value={nuevaSala.nombre} /></label>
    <label class="field"><span>Estado</span>
      <select bind:value={nuevaSala.estado}>
        <option>DISPONIBLE</option><option>OCUPADA</option><option>LIMPIEZA</option><option>MANTENIMIENTO</option>
      </select>
    </label>
    <button type="submit">+ Sala</button>
  </form>
  <table>
    <thead><tr><th>Código</th><th>Nombre</th><th>Estado</th></tr></thead>
    <tbody>
      {#each salas as s (s.id)}
        <tr><td>{s.codigo}</td><td>{s.nombre ?? '—'}</td><td><span class="badge {s.estado?.toLowerCase()}">{s.estado}</span></td></tr>
      {:else}<tr><td colspan="3" class="empty">Sin salas.</td></tr>{/each}
    </tbody>
  </table>

  <h3>Programar cirugía</h3>
  <form onsubmit={programar} class="form-grid">
    <PacientePicker bind:value={nuevaCir.pacienteId} onPick={(id) => nuevaCir.pacienteId = id} />
    <select bind:value={nuevaCir.salaId} required>
      <option value="">— Sala —</option>
      {#each salas as s}<option value={s.id}>{s.codigo} {s.nombre ? '· ' + s.nombre : ''}</option>{/each}
    </select>
    <select bind:value={nuevaCir.episodioId} disabled={episodios.length === 0}>
      <option value="">— Episodio (opcional) —</option>
      {#each episodios as ep}<option value={ep.id}>{ep.numero} · {ep.tipo}</option>{/each}
    </select>
    <input placeholder="Procedimiento" bind:value={nuevaCir.procedimiento} required />
    <select bind:value={nuevaCir.cirujanoId}>
      <option value="">— Cirujano —</option>
      {#each profesionales as p (p.id)}<option value={p.nombre}>{p.nombre}</option>{/each}
    </select>
    <input type="datetime-local" bind:value={nuevaCir.fechaProgramada} required />
    <input type="number" min="15" step="15" placeholder="Duración (min)" bind:value={nuevaCir.duracionEstimadaMin} />
    <button type="submit" class="full">Programar</button>
  </form>

  <h3>Agenda (próximos 7 días)</h3>
  <table>
    <thead><tr><th>Fecha</th><th>Procedimiento</th><th>Paciente</th><th>Sala</th><th>Estado</th></tr></thead>
    <tbody>
      {#each agenda as c (c.id)}
        <tr>
          <td>{new Date(c.fechaProgramada).toLocaleString()}</td>
          <td>{c.procedimiento}</td>
          <td>{etiquetaVisible(nombresPac, c.pacienteId)}</td>
          <td>{salas.find(s => s.id === c.salaId)?.nombre || salas.find(s => s.id === c.salaId)?.codigo || '—'}</td>
          <td><span class="badge">{c.estado}</span></td>
        </tr>
      {:else}<tr><td colspan="5" class="empty">Sin cirugías programadas.</td></tr>{/each}
    </tbody>
  </table>
</section>

<style>
  .view-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .view-header h2 { margin: 0; font-size: 1.4rem; color: #111827; }
  .muted { margin: 0.2rem 0 0; color: #6b7280; font-size: 0.85rem; }
  button { padding: .5rem .9rem; border: 0; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border-radius: 8px; cursor: pointer; font: inherit; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button:hover:not(:disabled) { transform: translateY(-1px); }
  button:disabled { opacity: .55; cursor: not-allowed; box-shadow: none; }
  h3 { margin: 1.5rem 0 .5rem; font-size: 1rem; color: #166534; }
  .row { display: flex; gap: .75rem; flex-wrap: wrap; margin-bottom: .5rem; align-items: flex-end; }
  .field { display: flex; flex-direction: column; gap: .3rem; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .form-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: .5rem; align-items: end; }
  .form-grid > :global(.picker), .form-grid > input, .form-grid > select, .form-grid > button, .row input, .row select { padding: .5rem .65rem; border-radius: 8px; border: 1px solid #e5e7eb; font: inherit; background: #fff; color: #111827; }
  .form-grid > :global(.picker):focus, .form-grid > input:focus, .form-grid > select:focus, .row input:focus, .row select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .form-grid > button, .row button { border: 0; }
  .full { grid-column: span 3; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; padding: .55rem .8rem; background: #f0fdf4; }
  td { padding: .5rem .8rem; border-top: 1px solid #f3f4f6; text-align: left; font-size: .9rem; color: #111827; }
  .badge { padding: .15rem .55rem; border-radius: 999px; font-size: .75rem; background: #f3f4f6; color: #6b7280; }
  .badge.disponible { background: #dcfce7; color: #166534; }
  .badge.ocupada { background: #fee2e2; color: #dc2626; }
  .badge.limpieza { background: #fef3c7; color: #d97706; }
  .empty { text-align: center; color: #9ca3af; font-style: italic; }
  .err { color: #dc2626; }
</style>
